import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Descriptions, Empty, Switch, Tag, message } from 'antd';
import { API } from '@/services/api.service';

/**
 * How the package behaves — the handful of decisions that change every figure it
 * produces, kept visible rather than buried in code.
 *
 * @param {Object} props - Component props
 * @param {Object} props.reportingPackage - The selected package
 * @returns {JSX.Element} Package settings
 */
export default function PackagePanel({ reportingPackage }) {
  const { t } = useTranslation(['tables', 'messages']);
  const gr = (key) => t(`groupReporting.${key}`, { ns: 'tables' });
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: (payload) =>
      API.groupReporting.updatePackage(reportingPackage._id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-reporting-packages'] });
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  if (!reportingPackage) return <Empty />;

  const toggle = (field) => (checked) =>
    updateMutation.mutate({ [field]: checked });

  return (
    <Card size="small" title={gr('packageSettings')}>
      <Descriptions bordered column={{ xs: 1, md: 2 }} size="small">
        <Descriptions.Item label={gr('selectPackage')}>
          {reportingPackage.code} — {reportingPackage.name}
        </Descriptions.Item>
        <Descriptions.Item label="Valuta">
          {reportingPackage.sourceCurrency} → {reportingPackage.targetCurrency}
        </Descriptions.Item>
        <Descriptions.Item label={gr('cumulative')}>
          <Switch
            checked={reportingPackage.cumulative}
            onChange={toggle('cumulative')}
            loading={updateMutation.isPending}
          />
        </Descriptions.Item>
        <Descriptions.Item label={gr('retranslate')}>
          <Switch
            checked={reportingPackage.retranslateFullPeriod}
            onChange={toggle('retranslateFullPeriod')}
            loading={updateMutation.isPending}
          />
        </Descriptions.Item>
        <Descriptions.Item label={gr('freeze')}>
          <Switch
            checked={reportingPackage.freezeSubmittedPeriods}
            onChange={toggle('freezeSubmittedPeriods')}
            loading={updateMutation.isPending}
          />
        </Descriptions.Item>
        <Descriptions.Item label={gr('fallbackUnit')}>
          <Tag>{reportingPackage.fallbacks?.unit || '—'}</Tag>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}
